// The home menu's Debug entry (docs/05-Tools/Debug Menu.md), shown only
// where the Worker says its debug tools are open (staging, development):
// three clears, each armed by a first tap and run by a second, with a line
// saying what went. Everything it does is this account's own data.
import { useEffect, useState } from 'react';
import type { NightResponse } from '../../systems/api/types';
import { formatShortDate } from '../../systems/clock/serverClock';
import { clearData, type DebugScope } from '../../systems/debug/debugTools';
import { Button } from '../components/Button';

/** An armed button disarms by itself after this long. */
const ARMED_MS = 4000;

interface Tool {
  scope: DebugScope;
  label: string;
  what: string;
}

export function DebugPanel({ night }: { night: NightResponse }) {
  const [armed, setArmed] = useState<DebugScope | null>(null);
  const [busy, setBusy] = useState<DebugScope | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(null), ARMED_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  const past = night.date !== night.tonight;
  const nightName = past ? formatShortDate(night.date) : 'tonight';
  const tools: Tool[] = [
    { scope: 'player', label: 'Clear Player Data', what: 'Every play, share link and horoscope of this account.' },
    { scope: 'night', label: 'Clear Day Data', what: `Every level played ${nightName}, the week's XL levels too, with their share links and the horoscope.` },
    { scope: 'horoscope', label: 'Clear Horoscope', what: `The horoscope of ${nightName}, so it can be written again.` },
  ];

  const tap = async (tool: Tool) => {
    if (armed !== tool.scope) {
      setArmed(tool.scope);
      setNote(null);
      return;
    }
    setArmed(null);
    setBusy(tool.scope);
    try {
      setNote(await clearData(tool.scope, past ? night.date : null));
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Could not clear.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="debug-tools">
      <p>Testing tools, never on production. A clear is immediate and cannot be undone; it touches only this account.</p>
      {tools.map((tool) => (
        <div key={tool.scope} className="debug-tool">
          <Button variant="ghost" small className={armed === tool.scope ? 'is-armed' : undefined} disabled={busy !== null} onClick={() => void tap(tool)}>
            {busy === tool.scope ? 'Clearing…' : armed === tool.scope ? 'Tap again to confirm' : tool.label}
          </Button>
          <span className="debug-tool-what">{tool.what}</span>
        </div>
      ))}
      {note && (
        <p className="debug-note" role="status">
          {note}
        </p>
      )}
    </div>
  );
}
